import { createFileRoute } from '@tanstack/react-router'
import { ExternalLinkIcon, InfoIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useServiceGroups } from '@/api/hooks/useServiceGroups'
import { ChangeBadge } from '@/components/atoms/change-badge'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import { GenericListSkeleton } from '@/components/molecules/generic-skeletons'
import PageHeader from '@/components/molecules/page-header'
import { PlayVideoButton } from '@/components/molecules/play-video-button'
import { ResponsiveTabs } from '@/components/molecules/responsive-tabs'
import ServiceCard from '@/components/molecules/service-card'
import ServiceRecommendation from '@/components/molecules/service-recommendation'
import { CreateErrandDialog } from '@/components/organisms/create-errand-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { TabsContent } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FIND_SUPPORT_VIDEO } from '@/config/videos'
import { useRegen } from '@/contexts/regen-context'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useMarkServicesSeen, useServiceChanges } from '@/hooks/useServiceChanges'
import { lastRunMetById, metChange, sortServicesByMet } from '@/utils/service-diff.utils'

const ServicesPage = () => {
  const { t } = useTranslation()
  const { selectedIndividual, selectedIndividualId } = useIndividuals()
  const { data: serviceGroups = [], isLoading: serviceGroupsLoading } =
    useServiceGroups(selectedIndividualId)
  const regen = useRegen()

  const [activeTab, setActiveTab] = useState<string>()
  const [errandDialogState, setErrandDialogState] = useState<{ open: boolean; serviceId?: string }>(
    {
      open: false,
    },
  )

  // Opening this page clears the sidebar's change dot. Stamped with the newest
  // changed-generation timestamp rather than "now", so a regen that lands while
  // the page is open is covered too — and one arriving after the user leaves
  // still lights the dot again.
  const { latestChangeAt } = useServiceChanges(selectedIndividualId)
  const markServicesSeen = useMarkServicesSeen()

  useEffect(() => {
    if (!selectedIndividualId || latestChangeAt === 0) return
    markServicesSeen(selectedIndividualId, latestChangeAt)
  }, [selectedIndividualId, latestChangeAt, markServicesSeen])

  const hasAnyGeneration = serviceGroups.some(g => g.generation)

  const activeGroup = useMemo(() => {
    if (!serviceGroups.length) return undefined

    return serviceGroups.find(g => g.id === activeTab) ?? serviceGroups[0]
  }, [serviceGroups, activeTab])

  const sortedServices = useMemo(() => {
    if (!activeGroup) return []

    return sortServicesByMet(activeGroup.services, activeGroup.generation?.services)
  }, [activeGroup])

  // Per-group { serviceId: previous met } from the last run, for change badges.
  const previousMetByGroup = useMemo(
    () =>
      Object.fromEntries(
        serviceGroups.map(group => [group.id, lastRunMetById(group.generation?.lastRun?.services)]),
      ),
    [serviceGroups],
  )

  const municipalityLinks = selectedIndividual?.municipality.links

  return (
    <StateSwitcher
      isLoading={serviceGroupsLoading}
      loadingState={<GenericListSkeleton rowHeight={160} />}
    >
      <div className="@container/page space-y-8">
        <PageHeader
          body={t('servicesPage.header.description')}
          heading={t('servicesPage.header.title')}
          titleAction={
            <PlayVideoButton
              titleKey={FIND_SUPPORT_VIDEO.titleKey}
              youtubeId={FIND_SUPPORT_VIDEO.youtubeId}
            />
          }
        >
          <div className="flex flex-wrap gap-2">
            <CreateErrandDialog />
            {/* Only offered once there is actual new knowledge to generate from — showing
                this next to an individual with no documents or notes yet would promise an
                update with nothing to check support against. */}
            {regen.offer.shouldOffer && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button isLoading={regen.isRunning} onClick={regen.regenerate} variant="outline">
                    {t('servicesPage.updateButton')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('servicesPage.updateButtonTooltip')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </PageHeader>

        {!hasAnyGeneration && (
          <Alert variant="info">
            <InfoIcon />
            <AlertTitle>{t('servicesPage.firstTimeInfo.title')}</AlertTitle>
            <AlertDescription>
              <p>{t('servicesPage.firstTimeInfo.description')}</p>
            </AlertDescription>
          </Alert>
        )}

        {hasAnyGeneration && regen.offer.shouldOffer && (
          <Alert variant="info">
            <InfoIcon />
            <AlertTitle>{t('servicesPage.updateAvailableInfo.title')}</AlertTitle>
            <AlertDescription>
              <p>{t('servicesPage.updateAvailableInfo.description')}</p>
            </AlertDescription>
          </Alert>
        )}

        {serviceGroups.length > 0 && (
          <ResponsiveTabs
            breakpoint="@7xl/page"
            className="card"
            controlsClassName="space-y-3"
            defaultValue={serviceGroups[0]?.id}
            header={<h4 className="text-lg">{t('servicesPage.selectAreaLabel')}</h4>}
            items={serviceGroups.map(group => ({ label: group.title, value: group.id }))}
            listClassName="w-full [&>*]:flex-1"
            onValueChange={setActiveTab}
            selectTriggerClassName="w-[200px]"
            value={activeTab}
          >
            {serviceGroups.map(group => {
              const groupSortedServices =
                activeGroup?.id === group.id ? sortedServices : group.services

              return (
                <TabsContent key={group.id} value={group.id}>
                  <div className="space-y-8">
                    <p className="text-text-secondary max-w-prose">{group.description}</p>
                    <div className="flex flex-col md:flex-row gap-8 md:gap-20">
                      <Field className="w-fit">
                        <h5>
                          {t('servicesPage.generalLinksLabel', { serviceGroup: group.title })}
                        </h5>
                        {group.links?.map(link => (
                          <a
                            href={link.url}
                            key={link.url}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            <Button size="inline" variant="link">
                              <ExternalLinkIcon />
                              {link.label}
                            </Button>
                          </a>
                        ))}
                      </Field>

                      {group.showMunicipalityLinks && (
                        <Field className="w-fit">
                          <h5>
                            {t('servicesPage.municipalityLinksLabel', {
                              municipality: selectedIndividual?.municipality.name,
                            })}
                          </h5>
                          {municipalityLinks?.lssInformationUrl && (
                            <a
                              href={municipalityLinks.lssInformationUrl}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              <Button size="inline" variant="link">
                                <ExternalLinkIcon />
                                {t('servicesPage.moreInformationMunicipalityButton', {
                                  municipality: selectedIndividual?.municipality.name,
                                })}
                              </Button>
                            </a>
                          )}
                          {municipalityLinks?.lssApplicationUrl && (
                            <a
                              href={municipalityLinks.lssApplicationUrl}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              <Button size="inline" variant="link">
                                <ExternalLinkIcon />
                                {t('servicesPage.applyOnlineButton')}
                              </Button>
                            </a>
                          )}
                        </Field>
                      )}
                    </div>

                    {group.generation && (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <h5 className="font-heading font-bold">
                            {t('servicesPage.assessmentLabel')}
                          </h5>
                          <ChangeBadge
                            change={metChange(group.generation.met, group.generation.lastRun?.met)}
                          />
                        </div>
                        <ServiceRecommendation generation={group.generation} />
                      </div>
                    )}

                    <div className="flex flex-col gap-4">
                      <h5 className="font-heading font-bold">
                        {t('servicesPage.servicesListLabel')}
                      </h5>
                      {groupSortedServices.map(service => {
                        const generation = group.generation?.services?.find(
                          gs => gs.id === service.id,
                        )
                        const previousMet = previousMetByGroup[group.id]?.[service.id]
                        const change = metChange(generation?.met, previousMet)

                        return (
                          <ServiceCard
                            change={change}
                            generation={generation}
                            key={service.title}
                            onStartErrand={serviceId =>
                              setErrandDialogState({ open: true, serviceId })
                            }
                            service={service}
                          />
                        )
                      })}
                    </div>
                  </div>
                </TabsContent>
              )
            })}
          </ResponsiveTabs>
        )}

        <CreateErrandDialog
          defaultServiceId={errandDialogState.serviceId}
          hideTrigger
          onOpenChange={open => setErrandDialogState(state => ({ ...state, open }))}
          open={errandDialogState.open}
        />
      </div>
    </StateSwitcher>
  )
}

export const Route = createFileRoute('/_authenticated/services')({
  component: ServicesPage,
})
